import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import React, { FC, useState, useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { updateAS2MessageEndpoint } from '../clients/HrpsCertificatesApi';
import { Heading, useTranslation } from '../components/blocks';
import { Button, Text, FileInput } from '../components/blocks/localization';
import { Tile, usePageMessaging } from '../components/composites';
import { RootState } from '../redux/types';
import { useSiteMapRouter } from '../utils/SiteMapRouter';

const UploadCertificatePage: FC<UploadCertificatePageProps> = ({ username }) => {
    const { t } = useTranslation('uploadCertificatePage');
    const { cancel } = useSiteMapRouter();
    const { showError, showSuccess } = usePageMessaging();
    const [certificateFile, setCertificateFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);

    const handleFileUpload = useCallback(
        (files: File[]) => {
            const file = files[0];
            if (!file) return;

            if (!file.name.toLowerCase().endsWith('.cer')) {
                showError(t('uploadCertificate-invalidFileType'));
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                showError(t('uploadCertificate-fileTooLarge'));
                return;
            }

            setCertificateFile(file);
        },
        [showError, t]
    );

    const handleUpdateCertificate = useCallback(async () => {
        if (!username || !certificateFile) return;

        setLoading(true);
        try {
            const fileContent = await certificateFile.text();
            const cleanedCertificate = fileContent
                .replace(/-----BEGIN CERTIFICATE-----/g, '')
                .replace(/-----END CERTIFICATE-----/g, '')
                .replace(/[\r\n]/g, '')
                .trim();

            const result = await updateAS2MessageEndpoint({
                endpointName: username,
                newCertificate: cleanedCertificate,
            });

            if (result.success) {
                showSuccess(t(result.message!) || t('uploadCertificate-successMessage'));
                setCertificateFile(null);
            } else {
                showError(t(result.message!) || t('uploadCertificate-errorMessage'));
            }
        } catch (error: any) {
            const errorCode = error?.code;
            let errorMessage;

            switch (errorCode) {
                case 'AccessDenied':
                    errorMessage = t('uploadCertificate-accessDeniedError');
                    break;
                case 'Dependency':
                    errorMessage = t('uploadCertificate-dependencyError');
                    break;
                case 'InvalidInput':
                    errorMessage = t('uploadCertificate-invalidInputError');
                    break;
                default:
                    errorMessage = t('uploadCertificate-errorMessage');
            }

            showError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [certificateFile, showError, showSuccess, t, username]);

    return (
        <Column>
            <Row>
                <Heading level={4}>
                    <Text>{t('uploadCertificate-heading')}</Text>
                </Heading>
            </Row>
            <Row>
                <Tile title={t('uploadCertificate-tileTitle')} width={'100%'}>
                    <Column>
                        <Row>
                            <FileInput
                                type={'single'}
                                uploadButtonLabel={t('uploadCertificate-chooseFile')}
                                accept={'.cer'}
                                onFileAttached={handleFileUpload}
                            />
                        </Row>
                        {certificateFile && (
                            <Row>
                                <Text>
                                    {t('uploadCertificate-selectedFile')}
                                    {': '}
                                    {certificateFile.name}
                                </Text>
                            </Row>
                        )}
                    </Column>
                </Tile>
            </Row>
            <Row>
                <Button type={'secondary'} onClick={cancel}>
                    {t('cancel-buttonLabel')}
                </Button>
                <Button
                    type={'primary'}
                    onClick={handleUpdateCertificate}
                    isBusy={loading}
                    disabled={!username || !certificateFile}
                >
                    {t('uploadCertificate-buttonLabel')}
                </Button>
            </Row>
        </Column>
    );
};

const mapStateToProps = ({ userReducer }: RootState) => {
    return {
        username: userReducer.user?.username,
    };
};

const connector = connect(mapStateToProps);
export type UploadCertificatePageProps = ConnectedProps<typeof connector>;
export default connector(UploadCertificatePage);
